import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon, ClockIcon } from 'lucide-react';
import { Button, Skeleton } from '@monksflow/monks-ui';
import { projectsListQuery, type ProjectListItem } from '@/entities/project';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTES } from '@/shared/config/routes';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';

const PREVIEW_LIMIT = 4;

const RowSkeleton = () => (
  <div
    data-testid="skeleton-loader"
    className="flex h-16.5 items-center gap-sm rounded-lg border border-border-light bg-transparent p-sm"
  >
    <Skeleton className="size-10 shrink-0" />
    <div className="flex flex-1 flex-col gap-2xs">
      <Skeleton className="h-3.5 w-1/2" />
      <Skeleton className="h-3 w-1/3" />
    </div>
  </div>
);

const ProjectRow = ({ project }: { project: ProjectListItem }) => {
  const { t } = useTranslation('springboard');

  // The folder route is project-scoped and both ids are UUIDs, so the wrong one
  // reaches the guard as an unknown scope and answers 403 rather than failing
  // validation. No fallback to the internal `id`.
  const { platformProjectId } = project;

  const body = (
    <>
      <div className="flex size-10 shrink-0 items-center justify-center rounded-sm bg-secondary">
        <ClockIcon className="size-4 text-foreground-subtle" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3xs">
        <span className="truncate text-sm leading-5 font-medium text-foreground">
          {project.title ?? t('dashboard.recentProjects.untitledProject')}
        </span>
        <span className="truncate text-xs leading-4 text-muted-foreground">
          {t('dashboard.recentProjects.lastActivity', {
            date: formatRelativeDate(project.activity.lastActivityAt),
          })}
        </span>
      </div>
    </>
  );

  const surface =
    'flex-row items-center gap-sm gap-y-0 rounded-lg border border-border-light bg-transparent p-sm shadow-none transition-[border-color] duration-fast ease-out';

  // A card is a surface, not a control, so the row becomes a link rather than a
  // click handler — which is also what gives it a tab stop, Enter, middle-click
  // and open-in-new-tab. Without an id there is nowhere to go, so it stays inert
  // and stops advertising otherwise.
  if (!platformProjectId) {
    return <div className={`flex ${surface}`}>{body}</div>;
  }

  return (
    <Link
      to={ROUTES.PROJECT_DETAIL(platformProjectId)}
      className={`flex ${surface} hover:border-border-strong focus-visible:focus-ring`}
    >
      {body}
    </Link>
  );
};

export const RecentProjects = () => {
  const { t } = useTranslation('springboard');

  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId();

  const { data, isPending } = useQuery(
    projectsListQuery(workspaceId, {
      page: 1,
      pageSize: PREVIEW_LIMIT,
      sortBy: 'updated_at',
    }),
  );

  const projects = data?.items ?? [];

  if (!isPending && projects.length === 0) {
    return null;
  }

  return (
    <section className="content-block gap-md" data-testid="home-recent-projects">
      <div className="flex items-center justify-between gap-md">
        <h2 className="type-heading-sm leading-5 tracking-[-0.02em]">
          {t('dashboard.recentProjects.recentProjects')}
        </h2>
        <Button variant="ghost" size="sm" onClick={() => void navigate(ROUTES.PROJECTS)}>
          {t('dashboard.recentProjects.allProjects')}
          <ArrowRightIcon className="size-4" />
        </Button>
      </div>

      <div className="grid w-full grid-cols-1 gap-grid md:grid-cols-2 lg:grid-cols-4">
        {isPending
          ? Array.from({ length: PREVIEW_LIMIT }).map((_, i) => <RowSkeleton key={i} />)
          : projects.map((project) => <ProjectRow key={project.id} project={project} />)}
      </div>
    </section>
  );
};
